<script setup lang="ts">
  import { ExternalRoute } from "@/constants/externalRoute"
  import TrackingService from "@/services/TrackingService"
  import { whenever } from "@vueuse/core"
  import { computed } from "vue"
  import ErrorMessage from "@/components/ErrorMessage.vue"
  import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
  } from "@/components/ui/card"
  import { DialogTrigger } from "@/components/ui/dialog"
  import { Link } from "@/components/ui/link"
  import Stepper from "@/components/ui/stepper/Stepper.vue"
  import { StepperItemState } from "@/components/ui/stepper/StepperTypes"

  import CancelMoveOutDialog from "./CancelMoveOutDialog.vue"
  import { MoveOutSupportingText } from "./moveOutTimelineHelper"
  import { useMoveOutTimeline } from "./useMoveOutTimeline"

  const props = defineProps<{
    // Shows the last step as still to come, ahead of the closure animation.
    holdClosure?: boolean
    animateClosure?: boolean
  }>()

  const { showTimeline, isError, steps, supportingText, status } =
    useMoveOutTimeline()

  // Home requests the timeline on every visit, so Datadog can only tell
  // which sessions showed the card from this event.
  whenever(
    showTimeline,
    () => {
      TrackingService.sendEvent("move-out-timeline_impression", {
        eventCategory: "move_out",
        eventAction: "impression",
        statusType: status.value,
        ...(isError.value && { entityVariant: "error" }),
      })
    },
    // immediate: the app timeline page mounts with the timeline already loaded.
    { once: true, immediate: true },
  )

  const displayedSteps = computed(() =>
    steps.value.map((step, index) => {
      // Closure complete is always the last step.
      if (index !== steps.value.length - 1) return step

      if (props.holdClosure) {
        return {
          ...step,
          state: StepperItemState.IDLE,
          description: "",
          isCompleted: false,
        }
      }

      return { ...step, isAnimated: props.animateClosure }
    }),
  )
</script>

<template>
  <Card v-if="showTimeline" data-testid="move-out-timeline-card">
    <CardHeader>
      <CardTitle>Your Move Out Status</CardTitle>
    </CardHeader>
    <CardContent>
      <ErrorMessage v-if="isError" />
      <template v-else>
        <Stepper
          :steps="displayedSteps"
          :step-count="displayedSteps.length"
          class="pb-4 pt-3"
        />
        <p
          v-if="supportingText === MoveOutSupportingText.CANCEL_MOVE"
          class="my-4"
        >
          Your move out has begun. Plans changed?
          <CancelMoveOutDialog>
            <DialogTrigger class="link">Contact us to cancel</DialogTrigger>
          </CancelMoveOutDialog>
          your move.
        </p>
        <p
          v-else-if="
            supportingText === MoveOutSupportingText.READINGS_CONFIRMED
          "
          class="my-4"
        >
          Thanks, we've got your final readings and we're preparing your final
          bill. Need to change something?
          <Link :to="ExternalRoute.contactUs" class="link">Contact us</Link>.
        </p>
      </template>
    </CardContent>
  </Card>
</template>
